import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { claimsApi, payersApi } from '../../services/api';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../components/ui/table';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import {
  Plus, MagnifyingGlass, FunnelSimple, Eye, PencilSimple,
  PaperPlaneTilt, FileText, CaretDown, CaretUp, ArrowsClockwise,
  CurrencyInr, CheckCircle, Clock, WarningCircle, XCircle,
} from '@phosphor-icons/react';
import { Toaster, toast } from 'sonner';

const CLAIM_STATUSES = [
  'registered', 'eligibility_pending', 'eligibility_verified',
  'preauth_pending', 'preauth_approved', 'preauth_rejected',
  'documents_pending', 'documents_complete', 'coding_pending', 'coding_complete',
  'bill_preparation', 'claim_ready', 'submitted', 'query_raised',
  'denied', 'under_appeal', 'payment_pending', 'partially_settled', 'settled', 'closed', 'written_off'
];

const formatStatus = (s) => s?.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ') || '';

const STATUS_COLORS = {
  registered: 'bg-blue-100 text-blue-700',
  eligibility_pending: 'bg-amber-100 text-amber-700',
  eligibility_verified: 'bg-emerald-100 text-emerald-700',
  preauth_pending: 'bg-amber-100 text-amber-700',
  preauth_approved: 'bg-emerald-100 text-emerald-700',
  preauth_rejected: 'bg-red-100 text-red-700',
  documents_pending: 'bg-orange-100 text-orange-700',
  documents_complete: 'bg-teal-100 text-teal-700',
  coding_pending: 'bg-amber-100 text-amber-700',
  coding_complete: 'bg-teal-100 text-teal-700',
  bill_preparation: 'bg-indigo-100 text-indigo-700',
  claim_ready: 'bg-sky-100 text-sky-700',
  submitted: 'bg-blue-100 text-blue-700',
  query_raised: 'bg-yellow-100 text-yellow-700',
  denied: 'bg-red-100 text-red-700',
  under_appeal: 'bg-orange-100 text-orange-700',
  payment_pending: 'bg-violet-100 text-violet-700',
  partially_settled: 'bg-cyan-100 text-cyan-700',
  settled: 'bg-emerald-100 text-emerald-700',
  closed: 'bg-slate-100 text-slate-600',
  written_off: 'bg-slate-100 text-slate-500',
};

const formatCurrency = (v) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(v || 0);

const ClaimsPage = () => {
  const navigate = useNavigate();
  const [claims, setClaims] = useState([]);
  const [payers, setPayers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [payerFilter, setPayerFilter] = useState('all');
  const [sortField, setSortField] = useState('created_at');
  const [sortDir, setSortDir] = useState('desc');
  const [page, setPage] = useState(1);
  const perPage = 20;

  useEffect(() => { loadData(); }, [statusFilter, payerFilter]);

  const loadData = async () => {
    try {
      const params = {};
      if (statusFilter !== 'all') params.status = statusFilter;
      if (payerFilter !== 'all') params.payer_id = payerFilter;
      if (search) params.search = search;
      const [claimsRes, payersRes] = await Promise.all([
        claimsApi.list(params),
        payersApi.listActive()
      ]);
      setClaims(claimsRes.data);
      setPayers(payersRes.data);
    } catch { toast.error('Failed to load claims'); }
    finally { setLoading(false); }
  };

  const handleSearch = (e) => { e.preventDefault(); setPage(1); loadData(); };

  const handleSubmitClaim = async (claimId, e) => {
    e.stopPropagation();
    try {
      await claimsApi.submit(claimId);
      toast.success('Claim submitted!');
      loadData();
    } catch { toast.error('Failed to submit claim'); }
  };

  // Sort
  const sorted = [...claims].sort((a, b) => {
    const av = (a[sortField] || '').toString().toLowerCase();
    const bv = (b[sortField] || '').toString().toLowerCase();
    return sortDir === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av);
  });

  const totalPages = Math.ceil(sorted.length / perPage);
  const paginated = sorted.slice((page - 1) * perPage, page * perPage);

  // Stats
  const stats = {
    total: claims.length,
    active: claims.filter(c => !['closed', 'settled', 'written_off'].includes(c.status)).length,
    settled: claims.filter(c => c.status === 'settled').length,
    denied: claims.filter(c => c.status === 'denied').length,
    totalBilled: claims.reduce((s, c) => s + (c.billed_amount || 0), 0),
  };

  const SortHeader = ({ field, children }) => (
    <button className="flex items-center gap-1 text-xs uppercase tracking-wider font-semibold"
      onClick={() => { setSortField(field); setSortDir(d => d === 'asc' ? 'desc' : 'asc'); }}>
      {children}
      {sortField === field && (sortDir === 'asc' ? <CaretUp className="h-3 w-3" /> : <CaretDown className="h-3 w-3" />)}
    </button>
  );

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-sky-500" /></div>;

  return (
    <div className="space-y-6" data-testid="claims-page">
      <Toaster position="top-right" richColors />

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-slate-800 font-['Outfit']">Cases</h1>
          <p className="text-sm text-slate-500 mt-1">Manage and track all insurance cases</p>
        </div>
        <Button className="bg-gradient-to-r from-sky-500 to-blue-600 text-white shadow-lg shadow-sky-500/25 gap-2"
          onClick={() => navigate('/claims/new')} data-testid="new-claim-btn">
          <Plus className="h-4 w-4" /> New Case
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-5 gap-3">
        {[
          { label: 'Total', value: stats.total, color: 'text-slate-800', icon: FileText },
          { label: 'Active', value: stats.active, color: 'text-sky-700', icon: Clock },
          { label: 'Settled', value: stats.settled, color: 'text-emerald-700', icon: CheckCircle },
          { label: 'Denied', value: stats.denied, color: 'text-red-600', icon: XCircle },
          { label: 'Total Billed', value: formatCurrency(stats.totalBilled), color: 'text-violet-700', icon: CurrencyInr },
        ].map(s => (
          <Card key={s.label} className="bg-white border border-slate-200 shadow-sm">
            <CardContent className="pt-3 pb-3 text-center">
              <p className="text-[10px] text-slate-500 font-medium uppercase tracking-wider">{s.label}</p>
              <p className={`text-xl font-bold ${s.color}`}>{s.value}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Filters */}
      <div className="flex gap-3 items-center flex-wrap">
        <form onSubmit={handleSearch} className="relative flex-1 min-w-[200px] max-w-md">
          <MagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input placeholder="Search by claim # or patient..." value={search}
            onChange={e => setSearch(e.target.value)} className="pl-9 border-slate-300" data-testid="claim-search-input" />
        </form>
        <Button variant="outline" size="sm" onClick={() => { setSearch(''); setStatusFilter('all'); setPayerFilter('all'); loadData(); }} className="border-slate-300">
          <ArrowsClockwise className="h-4 w-4" />
        </Button>
        <FunnelSimple className="h-4 w-4 text-slate-400" />
        <Select value={statusFilter} onValueChange={v => { setStatusFilter(v); setPage(1); }}>
          <SelectTrigger className="w-40 border-slate-300" data-testid="status-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Statuses</SelectItem>
            {CLAIM_STATUSES.map(s => <SelectItem key={s} value={s}>{formatStatus(s)}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={payerFilter} onValueChange={v => { setPayerFilter(v); setPage(1); }}>
          <SelectTrigger className="w-40 border-slate-300" data-testid="payer-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Payers</SelectItem>
            {payers.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <span className="text-xs text-slate-400">{sorted.length} claim{sorted.length !== 1 ? 's' : ''}</span>
      </div>

      {/* Table */}
      <Card className="bg-white border border-slate-200 shadow-sm" data-testid="claims-table">
        <CardContent className="p-0">
          {claims.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <FileText className="h-12 w-12 text-slate-300 mb-3" weight="light" />
              <p className="text-sm text-slate-600">No cases found</p>
              <Button className="mt-3 bg-gradient-to-r from-sky-500 to-blue-600 text-white gap-2" onClick={() => navigate('/claims/new')}>
                <Plus className="h-4 w-4" /> Create Case
              </Button>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead className="text-slate-500"><SortHeader field="claim_number">Case No</SortHeader></TableHead>
                    <TableHead className="text-xs uppercase tracking-wider text-slate-500">Claim No</TableHead>
                    <TableHead className="text-slate-500"><SortHeader field="patient_name">Patient</SortHeader></TableHead>
                    <TableHead className="text-xs uppercase tracking-wider text-slate-500">Payer / TPA</TableHead>
                    <TableHead className="text-xs uppercase tracking-wider text-slate-500">Dept</TableHead>
                    <TableHead className="text-xs uppercase tracking-wider text-slate-500">Type</TableHead>
                    <TableHead className="text-slate-500"><SortHeader field="billed_amount">Billed</SortHeader></TableHead>
                    <TableHead className="text-slate-500"><SortHeader field="status">Status</SortHeader></TableHead>
                    <TableHead className="text-slate-500"><SortHeader field="created_at">Date</SortHeader></TableHead>
                    <TableHead className="text-xs uppercase tracking-wider text-slate-500 text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {paginated.map(claim => (
                    <TableRow key={claim.id} className="hover:bg-sky-50/50 cursor-pointer transition-colors"
                      onClick={() => navigate(`/claims/${claim.id}`)} data-testid={`claim-row-${claim.id}`}>
                      <TableCell>
                        <span className="font-semibold text-sky-700 text-sm">{claim.claim_number}</span>
                      </TableCell>
                      <TableCell>
                        {claim.insurance_claim_no
                          ? <span className="text-xs font-mono font-medium text-orange-600">{claim.insurance_claim_no}</span>
                          : <span className="text-xs text-slate-300">-</span>}
                      </TableCell>
                      <TableCell>
                        <div>
                          <p className="font-medium text-slate-800 text-sm">{claim.patient_name || 'N/A'}</p>
                          {claim.uhid && <p className="text-[10px] text-slate-400 font-mono">{claim.uhid}</p>}
                        </div>
                      </TableCell>
                      <TableCell className="text-xs text-slate-600">{claim.payer_name || '-'}</TableCell>
                      <TableCell>
                        <span className="text-xs text-slate-500">{claim.department || 'General'}</span>
                      </TableCell>
                      <TableCell>
                        {claim.claim_type && (
                          <Badge className={`border-0 text-[9px] uppercase ${claim.claim_type === 'cashless' ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}>
                            {claim.claim_type}
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell>
                        <span className="font-medium text-slate-800 text-sm">{formatCurrency(claim.billed_amount)}</span>
                      </TableCell>
                      <TableCell>
                        <Badge className={`${STATUS_COLORS[claim.status] || 'bg-slate-100 text-slate-600'} border-0 text-[10px]`}>
                          {formatStatus(claim.status)}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-xs text-slate-500">
                        {new Date(claim.created_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-0.5">
                          <Button variant="ghost" size="sm" className="hover:bg-sky-50 text-sky-600 h-7 w-7 p-0"
                            onClick={(e) => { e.stopPropagation(); navigate(`/claims/${claim.id}`); }}
                            data-testid={`view-claim-${claim.id}`} title="View / Edit">
                            <Eye className="h-3.5 w-3.5" />
                          </Button>
                          {claim.status === 'claim_ready' && (
                            <Button variant="ghost" size="sm" className="hover:bg-emerald-50 text-emerald-600 h-7 w-7 p-0"
                              onClick={(e) => handleSubmitClaim(claim.id, e)}
                              data-testid={`submit-claim-${claim.id}`} title="Submit">
                              <PaperPlaneTilt className="h-3.5 w-3.5" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between px-4 py-3 border-t border-slate-100">
              <span className="text-xs text-slate-400">
                Showing {((page - 1) * perPage) + 1}-{Math.min(page * perPage, sorted.length)} of {sorted.length}
              </span>
              <div className="flex gap-1">
                <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)} className="h-7 text-xs">Prev</Button>
                {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
                  const pn = page <= 3 ? i + 1 : page + i - 2;
                  if (pn < 1 || pn > totalPages) return null;
                  return (
                    <Button key={pn} variant={pn === page ? 'default' : 'outline'} size="sm"
                      onClick={() => setPage(pn)} className={`h-7 w-7 text-xs ${pn === page ? 'bg-sky-500 text-white' : ''}`}>{pn}</Button>
                  );
                })}
                <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)} className="h-7 text-xs">Next</Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default ClaimsPage;
